import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, ArrowRight, ArrowDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Reveal } from '../Reveal';
import { coach } from '../../config/coach';

const clients = coach.results.clients || [];

const Panel = ({ label, src, pos }) => (
  <div className="relative flex-1">
    <div className="relative w-full aspect-[3/4] overflow-hidden rounded-xl border border-white/10 bg-[#0a0a0a]">
      <img
        src={src}
        alt={label}
        className="w-full h-full object-cover img-grade"
        style={{ objectPosition: pos || '50% 25%' }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
      <span className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.3em] text-white/95 bg-black/55 backdrop-blur-sm border border-white/15 rounded-full px-3 py-1.5">
        {label}
      </span>
    </div>
  </div>
);

const ResultsSection = () => {
  const [index, setIndex] = useState(0);
  const total = clients.length;
  const go = (dir) => setIndex((prev) => (prev + dir + total) % total);
  const client = clients[index];

  if (!client) return null;

  return (
    <section id="results" className="relative bg-black py-24 sm:py-32" data-testid="results-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="text-center max-w-xl mx-auto mb-12 sm:mb-16">
          <Reveal><span className="kicker justify-center before:hidden">{coach.results.kicker}</span></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display font-bold uppercase text-white tracking-tight leading-[0.9] text-5xl sm:text-6xl lg:text-7xl mt-4">
              {coach.results.heading}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-5 text-white/55 text-sm sm:text-base">{coach.results.sub}</p>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="max-w-4xl mx-auto">
            <div className="glass-card p-4 sm:p-8">
              <div className="flex items-center justify-between mb-5">
                <span className="font-display uppercase tracking-[0.25em] text-white/90 text-xs sm:text-sm">
                  Transformation {index + 1}
                </span>
                <span className="text-white/40 text-xs sm:text-sm">of {total}</span>
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -24 }}
                  transition={{ duration: 0.35 }}
                  className="flex flex-col sm:flex-row items-center gap-3 sm:gap-5"
                  data-testid={`transformation-${index}`}
                >
                  <Panel label="Before" src={client.before} pos={client.beforePos} />
                  <div className="flex-shrink-0 w-10 h-10 rounded-full border border-white/20 bg-black flex items-center justify-center text-white">
                    <ArrowRight className="w-5 h-5 hidden sm:block" />
                    <ArrowDown className="w-5 h-5 sm:hidden" />
                  </div>
                  <Panel label="After" src={client.after} pos={client.afterPos} />
                </motion.div>
              </AnimatePresence>
            </div>

            {total > 1 && (
              <div className="flex items-center justify-center gap-4 mt-7">
                <button onClick={() => go(-1)} data-testid="results-prev-btn" className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center text-white hover:bg-white/10 transition-colors" aria-label="Previous">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="flex gap-2">
                  {clients.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setIndex(i)}
                      data-testid={`results-dot-${i}`}
                      aria-label={`Go to transformation ${i + 1}`}
                      className={`h-1.5 rounded-full transition-all ${index === i ? 'bg-white w-7' : 'bg-white/25 w-1.5 hover:bg-white/40'}`}
                    />
                  ))}
                </div>
                <button onClick={() => go(1)} data-testid="results-next-btn" className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center text-white hover:bg-white/10 transition-colors" aria-label="Next">
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default ResultsSection;
